import { fg } from '@atlaskit/platform-feature-flags';
import type { Perimeter } from '@atlassian/atl-context';
import { ExperiencePerformanceTypes, ExperienceTypes, UFOExperience } from '@atlassian/ufo';

import type {
	CategorizedStorageControlsCache,
	CategoryStorageType,
	FlatCategorizedStorageControlsCache,
} from '../../../types';
import { Logger } from '../logger';

import {
	getCategoryCacheUrlPerimeterLookup,
	getCommercialCategoryCacheUrlLookup,
} from './constants';

export class CacheLoader {
	private cacheUrl: string;

	/**
	 * Cache variable for storing categorized cookies cache
	 */
	private localResponseCache:
		| CategorizedStorageControlsCache
		| FlatCategorizedStorageControlsCache
		| undefined;

	/**
	 * Cache variable for storing a promise that's been fired by a different invocation of loadStorageControls
	 *
	 * Allows for many invocations to happen simultaneously, and for only one fetch to go out
	 */
	private localResponsePromiseCache: Promise<Response> | undefined;

	/**
	 * Cache variable for storing the promise returned from the response object's json method
	 *
	 * Ensures we don't invoke response.json multiple times
	 */
	private localResponseJsonPromiseCache:
		| Promise<CategorizedStorageControlsCache | FlatCategorizedStorageControlsCache | undefined>
		| undefined;

	constructor(storageType: CategoryStorageType, perimeter: Perimeter = 'commercial') {
		const useFlatFormat = fg('platform_bsc_flat_pattern_file_structure');

		const cacheUrlLookup =
			getCategoryCacheUrlPerimeterLookup(useFlatFormat)[perimeter] ||
			getCommercialCategoryCacheUrlLookup(useFlatFormat);
		this.cacheUrl = cacheUrlLookup[storageType];
	}

	clearStorageControlsDataCache(): void {
		this.localResponseCache = undefined;
		this.localResponsePromiseCache = undefined;
		this.localResponseJsonPromiseCache = undefined;
	}

	/**
	 * Unpacks the response promise from the categories CDN
	 *
	 * errors if that request was unsuccessful, returns the categories data otherwise
	 *
	 */
	private async handleCategoriesResponse(responsePromise: Promise<Response> | undefined) {
		if (!responsePromise) {
			throw new Error(`Failed to fetch Cookies Cache! Cached promise was undefined`);
		}

		const response = await responsePromise;
		if (!response.ok) {
			throw new Error(`Failed to fetch Cookies Cache! status: ${response.status}`);
		}
		// Checks if we've already fired the response.json() promise
		this.localResponseJsonPromiseCache = this.localResponseJsonPromiseCache || response?.json();

		return this.localResponseJsonPromiseCache;
	}

	async loadStorageControlsData(): Promise<
		CategorizedStorageControlsCache | FlatCategorizedStorageControlsCache | undefined
	> {
		const isFetchTrackingSampled = !Math.floor(Math.random() * 100);
		let fetchExperience: UFOExperience | null = null;

		if (isFetchTrackingSampled) {
			fetchExperience = new UFOExperience('browser-storage-controls-fetch-time', {
				type: ExperienceTypes.Load,
				performanceType: ExperiencePerformanceTypes.Custom,
				platform: { component: 'browser-storage-controls' },
			});
			fetchExperience.start();
		}

		if (this.localResponseCache) {
			if (fetchExperience) {
				fetchExperience.success();
			}
			return this.localResponseCache;
		} else {
			// Always use Prod CDN for prod/local/dev/staging environments, but stub the route for automation tests
			try {
				if (!this.localResponsePromiseCache) {
					Logger.debug(`Loading cache ${this.cacheUrl}`);
					// invoke CDN and cache the promise
					this.localResponsePromiseCache = fetch(this.cacheUrl);
				}

				// unpack response
				this.localResponseCache = await this.handleCategoriesResponse(
					this.localResponsePromiseCache,
				);

				if (fetchExperience) {
					fetchExperience.success();
				}

				return this.localResponseCache;
			} catch (e: any) {
				if (fetchExperience) {
					fetchExperience.failure({ metadata: { error: String(e) } });
				}

				Logger.errorWithOperationalEvent({
					action: 'loadStorageControlsError',
					message: e.message || '',
				});

				this.clearStorageControlsDataCache();
			}
		}
	}
}

export {
	getCategoryCacheUrlPerimeterLookup,
	getCommercialCategoryCacheUrlLookup,
} from './constants';
